import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'
import Cookies from 'js-cookie'
import classNames from 'classnames'

import GiftingModal from '../gifting-flow/GiftingModal'
import { ConfirmationAlert } from 'modules/contribution/components/ConfirmationAlert'
import { GiftingPageHeader } from './GiftingPageHeader'
import { ContributionLimits, IUser } from 'modules/contribution/types'
import { getConfirmation } from 'utils/helpers'

import { getQuery } from 'hooks/getQuery'
import { buildPath } from 'routes'
import { FrequencyGifter } from 'modules/gift/types'
import { PartnerName } from 'modules/profile/userInterfaces'
import { FundBacker } from 'modules/fund/components/FundBackers'

import SetupModal from './SetupModal'
import FundSection from './FundSection'
import GiftSection from './GiftSection'
import BirthdayReminderModal from './BirthdayReminderModal'

import styles from './GiftingPage.module.scss'

const GiftingPage = ({
  fund,
  defaultFrequency,
  showSetupModal,
  user,
  refetchCurrentUser
}: Props) => {
  const history = useHistory()
  const giftingModalRef = useRef<any>(null)
  const setupModalRef = useRef<any>(null)
  const birthdayReminderModalRef = useRef<any>(null)
  const { canUpdateFund } = fund

  const [confirmation, setConfirmation] = useState<any>(null)
  const queryParams = getQuery()
  const handle = fund.handle

  const isAuthenticated = !!Cookies.get('authorization')

  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    setWidth(ref.current ? ref.current.offsetWidth : 0)
  }, [])

  useEffect(() => {
    function handleWindowResize() {
      setWidth(ref.current ? ref.current.offsetWidth : 0)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  useEffect(() => {
    const maybeConfirmation = getConfirmation()
    if (!isEmpty(maybeConfirmation)) {
      setConfirmation(maybeConfirmation)
    }
  }, [])

  useEffect(() => {
    if (handle) {
      const root = window as any
      let url = buildPath('giftingPage', { handle })
      if (queryParams) {
        url = `${url}?${queryParams.toString()}`
      }
      root.history.replaceState('', root.document.title, url)
    }
  }, [handle])

  useEffect(() => {
    if (showSetupModal && setupModalRef.current) {
      setupModalRef.current.open()
      if (queryParams) {
        queryParams.delete('setup')
        history.replace({
          search: queryParams.toString()
        })
      }
    }
  }, [setupModalRef.current])

  const userIsFundFollower =
    user && user?.followedFunds.find((ff: { id: string }) => ff.id === fund.id)
  const disableFollowFund =
    isAuthenticated && !!(fund.isBacker || userIsFundFollower)

  return (
    <div ref={ref}>
      <ConfirmationAlert
        confirmation={confirmation}
        setConfirmation={setConfirmation}
      />
      <SetupModal modalRef={setupModalRef} handle={handle} isNC529={fund.programManager === 'nc529'} />
      <BirthdayReminderModal ref={birthdayReminderModalRef} handle={handle} refetchCurrentUser={refetchCurrentUser} />

      {isAuthenticated && (
        <GiftingPageHeader handle={handle} canUpdateFund={canUpdateFund} />
      )}

      <div
        className={classNames('grid', styles.container, {
          [styles.containerTopPadding]: !isAuthenticated
        })}
      >
        <div
          className={classNames(
            'grid-desktop-2-11',
            'grid-tablet-1-12',
            'grid-mobile-1-6',
            styles.containerContent
          )}
        >
          <FundSection fund={fund} disableFollowFund={disableFollowFund} birthdayReminderModalRef={birthdayReminderModalRef} />

          <GiftSection
            defaultFrequency={defaultFrequency}
            disableFollowFund={disableFollowFund}
            fund={fund}
            giftingModalRef={giftingModalRef}
            birthdayReminderModalRef={birthdayReminderModalRef}
          />

          <GiftingModal
            modalRef={giftingModalRef}
            fund={fund}
            user={user}
            refetchCurrentUser={refetchCurrentUser}
          />
        </div>
      </div>
    </div>
  )
}

interface UserWithFollowedFunds extends IUser {
  id: string
  followedFunds: {
    id: string
  }[]
}

interface Props {
  fund: {
    id: string
    uuid: string
    handle: string
    programManager: string
    canUpdateFund: boolean
    countBackers: number
    childAgeLabel?: string
    yearsUntilCollege: number
    collegeEntryYear: number
    isBacker: boolean
    fundName: string
    fundOwner: {
      firstName: string
      fullName: string
      id: string
      partnerName: PartnerName
    }
    giftingPage: {
      ownerPhotoUrl: string
      photoUrl: string
      greeting: string
    }
    contributionLimits: ContributionLimits
  }
  defaultFrequency: FrequencyGifter
  showSetupModal: boolean
  user: UserWithFollowedFunds
  refetchCurrentUser: () => Promise<any>
}

export type GiftingPageView = 'default' | 'details'

export default GiftingPage
